/**
 * Future-purchase scenario projection.
 *
 * M3 expansions over M1:
 *   - `projectScenarioFromEvents` accepts a flat (date, amountUsd) event list,
 *     bypassing the rule expander so seed data with 145+ planned amounts can
 *     project without being modeled as 145 individual rules.
 *   - `expandSalary` turns a SalaryAssumption into a paycheck-by-paycheck event
 *     list, applying the annual raise compounding per paycheck.
 *
 * M1-era helpers (`expandRule`, `expandScenarioEvents`, `projectScenario`,
 * `firstRowReachingTarget`) remain unchanged.
 */

import { Decimal, ZERO, ONE } from './money.js';
import type { Btc, Usd } from './money.js';
import type {
  BtcPriceModel,
  ComputedPurchaseRow,
  PurchasePlanRule,
  PurchaseScenario,
  PurchaseTransaction,
  SalaryAssumption,
} from './types.js';
import { projectBtcPrice } from './price-model.js';

const MS_PER_DAY = 86_400_000;

/**
 * Expand a single rule into a list of `(date, amountUsd)` events between [from, to].
 */
export function expandRule(
  rule: PurchasePlanRule,
  from: Date,
  to: Date,
): Array<{ date: Date; amountUsd: Usd; rule: PurchasePlanRule }> {
  if (!rule.enabled) return [];

  const out: Array<{ date: Date; amountUsd: Usd; rule: PurchasePlanRule }> = [];
  const startMs = rule.startDate.getTime();
  const endMs = rule.endDate ? rule.endDate.getTime() : to.getTime();

  if (rule.kind === 'oneTime') {
    if (startMs >= from.getTime() && startMs <= to.getTime()) {
      out.push({ date: new Date(startMs), amountUsd: rule.amountUsd, rule });
    }
    return out;
  }

  // recurring
  const cadence = rule.cadenceDays?.toNumber() ?? 30.44;
  const monthlyGrowth = rule.growthAprPct
    ? ONE.plus(rule.growthAprPct.div(365)).pow(cadence).minus(ONE)
    : ZERO;

  let cursor = startMs;
  let amount = rule.amountUsd;
  let step = 0;
  const stepMs = cadence * MS_PER_DAY;

  while (cursor <= Math.min(endMs, to.getTime())) {
    if (cursor >= from.getTime()) {
      out.push({ date: new Date(cursor), amountUsd: amount, rule });
    }
    cursor += stepMs;
    if (!monthlyGrowth.isZero() && step > 0) {
      amount = amount.times(ONE.plus(monthlyGrowth));
    }
    step += 1;
  }

  return out;
}

/**
 * Expand all rules in a scenario into a date-sorted event list.
 */
export function expandScenarioEvents(
  scenario: PurchaseScenario,
  from: Date,
  to: Date,
): Array<{ date: Date; amountUsd: Usd; rule: PurchasePlanRule }> {
  if (!scenario.enabled) return [];
  const events = scenario.rules.flatMap(r => expandRule(r, from, to));
  return events.sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * Project a scenario into a list of `ComputedPurchaseRow`s.
 *
 * @param actualTxs Optional anchor of actual purchases  -  included as 'actual' rows so
 *                  projected balance starts from the real BTC stack at the seam date.
 *                  Pass [] to project a pure scenario.
 * @param scenario  The scenario to project.
 * @param priceModel Price model used for the BTC price column.
 * @param windowFrom/windowTo Projection window. Events outside are skipped.
 */
export function projectScenario(
  actualTxs: PurchaseTransaction[],
  scenario: PurchaseScenario,
  priceModel: BtcPriceModel,
  windowFrom: Date,
  windowTo: Date,
): ComputedPurchaseRow[] {
  const rows: ComputedPurchaseRow[] = [];

  const actuals = [...actualTxs].sort((a, b) => a.date.getTime() - b.date.getTime());
  let btcBalance: Btc = ZERO;
  let cumulativeUsd: Usd = ZERO;

  // Emit actuals
  for (const t of actuals) {
    btcBalance = btcBalance.plus(t.btc);
    cumulativeUsd = cumulativeUsd.plus(t.usd);
    rows.push({
      date: t.date,
      purchaseUsd: t.usd,
      btcPrice: t.btc.isZero() ? ZERO : t.usd.div(t.btc),
      purchaseBtc: t.btc,
      btcBalance,
      cumulativeUsd,
      costBasis: btcBalance.isZero() ? ZERO : cumulativeUsd.div(btcBalance),
      note: t.note,
      source: 'actual',
      scenarioId: scenario.id,
    });
  }

  // Emit projected events
  const events = expandScenarioEvents(scenario, windowFrom, windowTo);
  for (const e of events) {
    const btcPrice = projectBtcPrice(e.date, priceModel);
    if (btcPrice.isZero()) continue;
    const purchaseBtc = e.amountUsd.div(btcPrice);
    btcBalance = btcBalance.plus(purchaseBtc);
    cumulativeUsd = cumulativeUsd.plus(e.amountUsd);
    rows.push({
      date: e.date,
      purchaseUsd: e.amountUsd,
      btcPrice,
      purchaseBtc,
      btcBalance,
      cumulativeUsd,
      costBasis: btcBalance.isZero() ? ZERO : cumulativeUsd.div(btcBalance),
      note: e.rule.note,
      source: 'projected',
      scenarioId: scenario.id,
    });
  }

  return rows;
}

/**
 * Find the first projected row at which the BTC balance reaches `targetBtc`. Returns
 * null if not reached within the row set. Used by the carry simulator to derive its
 * start date from a chosen accumulation scenario (SPEC §A Q11).
 */
export function firstRowReachingTarget(
  rows: ComputedPurchaseRow[],
  targetBtc: Btc,
): ComputedPurchaseRow | null {
  for (const r of rows) {
    if (r.btcBalance.greaterThanOrEqualTo(targetBtc)) return r;
  }
  return null;
}

// ----- M3: direct events list & salary expansion -----

export type PlannedEventCategory =
  | 'salary' | 'bonus' | 'tax_refund' | 'stock_sale' | 'profit_share'
  | 'loan' | 'manual' | 'other';

export interface PlannedEvent {
  date: Date;
  amountUsd: Usd;
  /** Optional explicit BTC amount. When set, overrides the price-model lookup  - 
   *  the engine will record this exact BTC amount and treat USD/BTC as the
   *  effective price for the row. Used when the seed has dedicated columns
   *  (workbook's near-term purchasing sheets) capturing both USD paid and BTC
   *  received under a different BTC price assumption than the fair-value model. */
  purchaseBtc?: Btc | undefined;
  category?: PlannedEventCategory | undefined;
  note?: string | undefined;
}

/**
 * Project from a flat events list. Same shape as `projectScenario` but takes events
 * directly  -  useful when the source is an aggregated bi-monthly amount list (e.g.
 * the workbook's near-term purchasing sheets).
 *
 * If an event provides `purchaseBtc`, that value is used directly and the effective
 * price is computed as USD/BTC; otherwise the BTC amount is derived from the price
 * model at the event's date.
 */
export function projectScenarioFromEvents(
  actualTxs: PurchaseTransaction[],
  events: PlannedEvent[],
  priceModel: BtcPriceModel,
  scenarioId: string,
): ComputedPurchaseRow[] {
  const rows: ComputedPurchaseRow[] = [];
  const actuals = [...actualTxs].sort((a, b) => a.date.getTime() - b.date.getTime());
  const sortedEvents = [...events].sort((a, b) => a.date.getTime() - b.date.getTime());

  let btcBalance: Btc = ZERO;
  let cumulativeUsd: Usd = ZERO;

  for (const t of actuals) {
    btcBalance = btcBalance.plus(t.btc);
    cumulativeUsd = cumulativeUsd.plus(t.usd);
    rows.push({
      date: t.date,
      purchaseUsd: t.usd,
      btcPrice: t.btc.isZero() ? ZERO : t.usd.div(t.btc),
      purchaseBtc: t.btc,
      btcBalance,
      cumulativeUsd,
      costBasis: btcBalance.isZero() ? ZERO : cumulativeUsd.div(btcBalance),
      note: t.note,
      source: 'actual',
      scenarioId,
    });
  }

  const lastActualMs = actuals.length > 0 ? actuals[actuals.length - 1]!.date.getTime() : -Infinity;

  for (const e of sortedEvents) {
    if (e.date.getTime() <= lastActualMs) continue;
    let purchaseBtc: Btc;
    let btcPrice: Usd;
    if (e.purchaseBtc !== undefined) {
      purchaseBtc = e.purchaseBtc;
      btcPrice = purchaseBtc.isZero() ? ZERO : e.amountUsd.div(purchaseBtc);
    } else {
      btcPrice = projectBtcPrice(e.date, priceModel);
      if (btcPrice.isZero()) continue;
      purchaseBtc = e.amountUsd.div(btcPrice);
    }
    btcBalance = btcBalance.plus(purchaseBtc);
    cumulativeUsd = cumulativeUsd.plus(e.amountUsd);
    rows.push({
      date: e.date,
      purchaseUsd: e.amountUsd,
      btcPrice,
      purchaseBtc,
      btcBalance,
      cumulativeUsd,
      costBasis: btcBalance.isZero() ? ZERO : cumulativeUsd.div(btcBalance),
      note: e.note,
      category: e.category,
      source: 'projected',
      scenarioId,
    });
  }

  return rows;
}

const DAYS_PER_PAYCHECK: Record<SalaryAssumption['paycheckFrequency'], number> = {
  weekly: 7,
  biweekly: 14,
  semimonthly: 15.21875,        // 365.25 / 24
  monthly: 30.4375,             // 365.25 / 12
};

const PAYCHECKS_PER_YEAR: Record<SalaryAssumption['paycheckFrequency'], number> = {
  weekly: 52,
  biweekly: 26,
  semimonthly: 24,
  monthly: 12,
};

/**
 * Expand a SalaryAssumption into per-paycheck PlannedEvents within [from, to].
 *
 * Investable amount per paycheck:
 *   - 'fixed': the user-set per-paycheck investable amount, regardless of salary
 *   - 'percent': (annualSalary / paychecksPerYear) × investablePercent
 *
 * Annual raise compounds per paycheck: paycheck `n` (0-indexed since startDate)
 * uses a salary multiplied by (1 + raise)^(n / paychecksPerYear).
 */
export function expandSalary(
  salary: SalaryAssumption,
  from: Date,
  to: Date,
): PlannedEvent[] {
  const out: PlannedEvent[] = [];
  const cadenceDays = DAYS_PER_PAYCHECK[salary.paycheckFrequency];
  const paychecksPerYear = PAYCHECKS_PER_YEAR[salary.paycheckFrequency];
  const stepMs = cadenceDays * 86_400_000;

  let cursor = salary.startDate.getTime();
  let n = 0;
  const endMs = to.getTime();

  while (cursor <= endMs) {
    if (cursor >= from.getTime()) {
      const yearsElapsed = new Decimal(n).div(paychecksPerYear);
      const raiseFactor = ONE.plus(salary.annualRaisePercent).pow(yearsElapsed);
      let investable: Usd;
      if (salary.investableMode === 'fixed') {
        const base = salary.investableFixedPerPaycheck ?? ZERO;
        investable = base.times(raiseFactor);
      } else {
        const pct = salary.investablePercent ?? ZERO;
        const grownAnnualSalary = salary.baseSalaryUsd.times(raiseFactor);
        investable = grownAnnualSalary.times(pct).div(paychecksPerYear);
      }
      if (investable.greaterThan(0)) {
        out.push({ date: new Date(cursor), amountUsd: investable, category: 'salary' });
      }
    }
    cursor += stepMs;
    n += 1;
  }

  return out;
}
